import React, { useContext, useEffect, useState, useRef } from 'react';
import Chart from 'react-apexcharts';
import { UserContext } from '../common/UserContext';
import axios from 'axios';
import config from '../../envconfig';
import { useNavigate } from 'react-router-dom';

const RoutingWidget = () => {
    const PAGE_SIZE = 5;
    const [data, setData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [chartHeight, setChartHeight] = useState(400); // Default height
    const [chartWidth, setChartWidth] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const chartContainerRef = useRef(null);
    const { UM_GroupId, UM_CompanyGrpID, selectedScpId, UM_CompanyID, UM_UserId, UM_UserCode, UM_DefaultSCPId } = useContext(UserContext);

    const navigate = useNavigate();
    // console.log("bfbdvkdnkewfnkfnksfwdldlznksdfskb545555%%%%%%%%%%%%%");

    useEffect(() => {
        const fetchRouteScpInfo = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/route-info/${selectedScpId}`);
                setData(response.data);
                setCsvData(response.data); // Assuming you want to use this for exporting CSV
                setCurrentPage(1);
            } catch (err) {
                console.error('Error:', err);
            }
        };

        fetchRouteScpInfo();
    }, [selectedScpId]);

    useEffect(() => {
        const handleResize = () => {
            if (chartContainerRef.current) {
                setChartHeight(chartContainerRef.current.clientHeight);
                setChartWidth(chartContainerRef.current.clientWidth || 0);
            }
        };

        const resizeObserver = new ResizeObserver(handleResize);
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        return () => {
            if (chartContainerRef.current) {
                resizeObserver.unobserve(chartContainerRef.current);
            }
        };
    }, []);

    const wrapXAxisLabel = (label, maxCharsPerLine = 12) => {
        const words = String(label || '').split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach((word) => {
            // Break very long continuous text (no spaces) into chunks.
            if (word.length > maxCharsPerLine) {
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = '';
                }
                for (let i = 0; i < word.length; i += maxCharsPerLine) {
                    lines.push(word.slice(i, i + maxCharsPerLine));
                }
                return;
            }

            const nextLine = currentLine ? `${currentLine} ${word}` : word;
            if (nextLine.length <= maxCharsPerLine) {
                currentLine = nextLine;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        });

        if (currentLine) lines.push(currentLine);
        return lines.length ? lines : [''];
    };

    const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
    const effectivePage = Math.min(currentPage, totalPages);
    const pageStart = (effectivePage - 1) * PAGE_SIZE;
    const paginatedRouteData = data.slice(pageStart, pageStart + PAGE_SIZE);
    const totalCount = data.length;
    const rangeStart = totalCount === 0 ? 0 : pageStart + 1;
    const rangeEnd = Math.min(pageStart + PAGE_SIZE, totalCount);

    const labelCharsPerLine = (() => {
        if (!paginatedRouteData.length) return 10;
        const safeWidth = chartWidth || 600;
        const pixelsPerCategory = safeWidth / paginatedRouteData.length;
        const estimatedChars = Math.floor(pixelsPerCategory / 12);
        return Math.max(4, Math.min(10, estimatedChars));
    })();

    const wrappedCategories = paginatedRouteData.map((item) =>
        wrapXAxisLabel(item.RUTL_Name || '', labelCharsPerLine)
    );

    const maxLabelLines = wrappedCategories.reduce(
        (maxLines, labelLines) => Math.max(maxLines, labelLines.length),
        1
    );
    const xAxisMinHeight = Math.max(48, maxLabelLines * 16);
    const xAxisMaxHeight = Math.max(120, maxLabelLines * 20);
    const yAxisMinHeight = 28;
    const paginationSpace = 24;
    const bottomPadding = Math.max(24, Math.min(56, maxLabelLines * 8)) + paginationSpace;

    const chartOptions = {
        chart: {
            id: 'bar-chart',
            toolbar: {
                show: false,
                tools: {
                    download: true,
                    selection: false,
                    zoom: false,
                    zoomin: false,
                    zoomout: false,
                    pan: false,
                    reset: false
                },
                export: {
                    csv: { show: true },
                    svg: { show: true },
                    png: { show: true }
                },

            },
            events: {
                dataPointSelection: (event, chartContext, { dataPointIndex }) => {
                    const selectedData = paginatedRouteData[dataPointIndex];
                    const routeName = selectedData.RUTL_Name || 'Unknown Route';
                    const count = selectedData.RouteMasterCount || 0;
                    console.log(`Bar clicked: Route Name - ${routeName}, Count - ${count}`);
                    navigate("/rmlist")
                }
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: false,
                columnWidth: '55%',
                colors: {
                    ranges: [{
                        from: 0,
                        to: 100, // You can set this to the maximum value or adjust as needed
                        color: '#00A7E2' // Red color
                    }]
                }
            }
        },
        grid: {
            padding: {
                left: 12,
                bottom: bottomPadding,
            },
        },
        xaxis: {
            categories: wrappedCategories,
            labels: {
                rotate: 0,
                trim: false,
                hideOverlappingLabels: false,
                minHeight: xAxisMinHeight,
                maxHeight: xAxisMaxHeight,
                style: {
                    fontSize: '11px',
                },
            },
            title: {
                text: 'Route Name',
                offsetY: 2,
                style: {
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#000',
                    // fontFamily: "sans-serif"

                },
            }
        },
        yaxis: {
            title: {
                text: 'Count'
            },
            labels: {
                minWidth: yAxisMinHeight,
                maxWidth: 36,
                style: {
                    fontSize: '11px',
                },
            },
        },
        title: {
            text: 'Route SCP Info',
            align: 'left',  // Aligns the title to the left
            margin: 10,
            offsetX: 0,     // Adjust this value to move the title more to the left if needed
            offsetY: 1,
            floating: false,
            style: {
                marginTop: '10px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#2B2A29',

            },
        },
        tooltip: {
            y: {
                formatter: (value, { dataPointIndex }) => {
                    const dataPoint = paginatedRouteData[dataPointIndex];
                    const shipmentCount = dataPoint ? dataPoint.ShipmentCount || 0 : 0;
                    return `SCP : ${value} , In Transit Shipments : ${shipmentCount}`;
                }
            }
        }
    };


    const chartSeries = [{
        name: '',
        data: paginatedRouteData.map(item => item.RouteMasterCount || 0)
    }];

    return (
        <div
            ref={chartContainerRef}
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <div style={{ width: '100%', height: `${chartHeight}px`, position: 'relative' }}>
                <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    width="100%"
                    height="100%"
                />
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: '4px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={effectivePage <= 1}
                        aria-label="Previous page"
                        title="Previous page"
                        style={{
                            cursor: effectivePage <= 1 ? 'not-allowed' : 'pointer',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            lineHeight: 1,
                            fontSize: '24px',
                            color: effectivePage <= 1 ? '#9CA3AF' : '#111827',
                        }}
                    >
                        {'<'}
                    </button>
                    <span style={{ fontSize: '15px', lineHeight: 1, color: '#111827' }}>{`${rangeStart} - ${rangeEnd} / ${totalCount}`}</span>
                    <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={effectivePage >= totalPages}
                        aria-label="Next page"
                        title="Next page"
                        style={{
                            cursor: effectivePage >= totalPages ? 'not-allowed' : 'pointer',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            lineHeight: 1,
                            fontSize: '24px',
                            color: effectivePage >= totalPages ? '#9CA3AF' : '#111827',
                        }}
                    >
                        {'>'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoutingWidget;
